import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

/**
 * Small, quiet charts for reports: stacked columns and a single-series line. Drawn as SVG at the
 * container's real width (text never scales), thin marks, hairline grid, a 2px surface gap between
 * stacked segments, and a readout on hover and keyboard focus. Every chart has a table twin on the
 * page, so the readout never gates a value.
 */

export interface ChartSeries {
  name: string;
  /** CSS colour, e.g. "var(--chart-1)". */
  color: string;
}

export interface ColumnPoint {
  key: string;
  /** Under the column, when there's room. */
  label: string;
  /** Full name for the readout ("Wednesday, October 21", "2 PM to 3 PM"). */
  title: string;
  /** One value per series, bottom to top. */
  values: number[];
  /** Extra readout lines ("12 missed · 3 hung up"). */
  detail?: string;
}

export interface LinePoint {
  key: string;
  label: string;
  title: string;
  value: number | null;
}

const PAD = { top: 10, right: 12, bottom: 28, left: 44 };

function useWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const set = () => setWidth(el.getBoundingClientRect().width);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

/** A rounded top and a square base: the data end is rounded, the baseline isn't. */
function columnPath(x: number, y: number, w: number, h: number, r: number): string {
  const rr = Math.min(r, w / 2, h);
  return `M${x},${y + h}V${y + rr}Q${x},${y} ${x + rr},${y}H${x + w - rr}Q${x + w},${y} ${x + w},${y + rr}V${y + h}Z`;
}

/** 0 and up to four clean steps to just above the largest value. */
export function niceTicks(max: number, count = 4): number[] {
  if (!(max > 0)) return [0, 1];
  const raw = max / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? raw;
  const top = Math.ceil(max / step) * step;
  const out: number[] = [];
  for (let v = 0; v <= top + step / 2; v += step) out.push(Math.round(v * 1000) / 1000);
  return out;
}

/** Which x labels fit: every one, or every n-th, always the last. */
function labelEvery(count: number, width: number, labelWidth: number): number {
  if (count <= 1) return 1;
  return Math.max(1, Math.ceil((count * labelWidth) / Math.max(1, width)));
}

function Readout({ x, plotWidth, children }: { x: number; plotWidth: number; children: ReactNode }) {
  // Keep the readout inside the chart: right of the point, or left of it near the end.
  const left = x > plotWidth * 0.6;
  return (
    <div className="chart-readout" role="status" style={left ? { right: plotWidth - x + PAD.right + 12 } : { left: x + PAD.left + 12 }}>
      {children}
    </div>
  );
}

export function ColumnChart({
  points,
  series,
  height = 220,
  format = (n) => n.toLocaleString(),
  label
}: {
  points: ColumnPoint[];
  series: ChartSeries[];
  height?: number;
  format?: (n: number) => string;
  /** What the chart shows, for screen readers ("Calls by day"). */
  label: string;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [active, setActive] = useState<number | null>(null);
  const plotW = Math.max(0, width - PAD.left - PAD.right);
  const plotH = height - PAD.top - PAD.bottom;
  const totals = points.map((p) => p.values.reduce((a, b) => a + b, 0));
  const ticks = niceTicks(Math.max(...totals, 0));
  const top = ticks[ticks.length - 1] || 1;
  const band = points.length ? plotW / points.length : 0;
  const barW = Math.max(2, Math.min(24, band * 0.62));
  const y = (v: number) => PAD.top + plotH - (v / top) * plotH;
  const every = labelEvery(points.length, plotW, 46);
  const onKey = (e: KeyboardEvent, i: number) => {
    const next = e.key === 'ArrowRight' ? i + 1 : e.key === 'ArrowLeft' ? i - 1 : null;
    if (next === null || next < 0 || next >= points.length) return;
    e.preventDefault();
    (e.currentTarget.parentElement?.children[next] as SVGElement | undefined)?.focus();
  };

  return (
    <div className="chart" ref={ref} style={{ height }} onMouseLeave={() => setActive(null)}>
      {width > 0 && (
        <svg width={width} height={height} role="group" aria-label={label}>
          {ticks.map((t) => (
            <g key={t} aria-hidden="true">
              <line x1={PAD.left} x2={width - PAD.right} y1={y(t)} y2={y(t)} className={t === 0 ? 'chart-base' : 'chart-grid'} />
              <text x={PAD.left - 8} y={y(t)} className="chart-tick" textAnchor="end" dominantBaseline="middle">
                {format(t)}
              </text>
            </g>
          ))}
          <g aria-hidden="true">
            {points.map((p, i) => {
              const cx = PAD.left + band * i + band / 2;
              let acc = 0;
              const segs = p.values.map((v, s) => {
                const y0 = y(acc);
                acc += v;
                return { s, v, y1: y(acc), y0 };
              });
              const visible = segs.filter((g) => g.v > 0);
              const topSeg = visible[visible.length - 1];
              return (
                <g key={p.key} className={active === i ? 'chart-col active' : 'chart-col'}>
                  {visible.map((g) => {
                    // A 2px surface gap between stacked segments.
                    const gap = g === visible[0] ? 0 : 2;
                    const h = Math.max(0, g.y0 - g.y1 - gap);
                    return g === topSeg ? (
                      <path key={g.s} d={columnPath(cx - barW / 2, g.y1, barW, h, 4)} fill={series[g.s]?.color} />
                    ) : (
                      <rect key={g.s} x={cx - barW / 2} y={g.y1} width={barW} height={h} fill={series[g.s]?.color} />
                    );
                  })}
                  {(i % every === 0 || i === points.length - 1) && (i === points.length - 1 || points.length - 1 - i >= every) && (
                    <text x={cx} y={height - 8} className="chart-tick" textAnchor="middle">
                      {p.label}
                    </text>
                  )}
                </g>
              );
            })}
          </g>
          <g>
            {points.map((p, i) => (
              // The whole band is the target, not just the painted column.
              <rect
                key={p.key}
                x={PAD.left + band * i}
                y={PAD.top}
                width={band}
                height={plotH}
                className="chart-hit"
                tabIndex={0}
                aria-label={`${p.title}: ${series.map((s, k) => `${format(p.values[k] ?? 0)} ${s.name.toLowerCase()}`).join(', ')}${p.detail ? `. ${p.detail}` : ''}`}
                onMouseEnter={() => setActive(i)}
                onFocus={() => setActive(i)}
                onBlur={() => setActive(null)}
                onKeyDown={(e) => onKey(e, i)}
              />
            ))}
          </g>
        </svg>
      )}
      {active !== null && points[active] && (
        <Readout x={band * active + band / 2} plotWidth={plotW}>
          <span className="chart-readout-title">{points[active].title}</span>
          {series
            .map((s, k) => ({ s, v: points[active]!.values[k] ?? 0 }))
            .reverse()
            .map(({ s, v }) => (
              <span key={s.name} className="chart-readout-row">
                <span className="chart-key" style={{ background: s.color }} />
                <strong>{format(v)}</strong> {s.name.toLowerCase()}
              </span>
            ))}
          {points[active].detail && <span className="chart-readout-detail">{points[active].detail}</span>}
        </Readout>
      )}
    </div>
  );
}

export function LineChart({
  points,
  color,
  height = 200,
  format,
  max,
  label,
  empty = 'No calls'
}: {
  points: LinePoint[];
  color: string;
  height?: number;
  format: (n: number) => string;
  /** A fixed top for the scale (1 for a share). */
  max?: number;
  label: string;
  /** The readout for a point with no value ("No texts"). */
  empty?: string;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [active, setActive] = useState<number | null>(null);
  const gradient = useId().replace(/:/g, '');
  const plotW = Math.max(0, width - PAD.left - PAD.right);
  const plotH = height - PAD.top - PAD.bottom;
  const values = points.map((p) => p.value).filter((v): v is number => v !== null);
  const ticks = max !== undefined ? [0, max / 4, max / 2, (3 * max) / 4, max] : niceTicks(Math.max(...values, 0));
  const top = ticks[ticks.length - 1] || 1;
  const step = points.length > 1 ? plotW / (points.length - 1) : 0;
  const x = (i: number) => PAD.left + (points.length > 1 ? step * i : plotW / 2);
  const y = (v: number) => PAD.top + plotH - (v / top) * plotH;
  const every = labelEvery(points.length, plotW, 46);

  // Runs of points with values: a gap where there were no calls.
  const runs: { i: number; v: number }[][] = [];
  points.forEach((p, i) => {
    if (p.value === null) return void runs.push([]);
    if (!runs.length) runs.push([]);
    runs[runs.length - 1]!.push({ i, v: p.value });
  });
  const lines = runs.filter((r) => r.length);
  const lastIdx = [...points.keys()].reverse().find((i) => points[i]!.value !== null);

  const nearest = (clientX: number, el: Element) => {
    const rx = clientX - el.getBoundingClientRect().left - PAD.left;
    return Math.max(0, Math.min(points.length - 1, Math.round(points.length > 1 ? rx / step : 0)));
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    setActive((a) => Math.max(0, Math.min(points.length - 1, (a ?? (e.key === 'ArrowRight' ? -1 : points.length)) + (e.key === 'ArrowRight' ? 1 : -1))));
  };
  const a = active !== null ? points[active] : undefined;

  return (
    <div className="chart" ref={ref} style={{ height }}>
      {width > 0 && (
        <svg
          width={width}
          height={height}
          role="img"
          aria-label={label}
          tabIndex={0}
          onKeyDown={onKey}
          onBlur={() => setActive(null)}
          onPointerMove={(e) => setActive(nearest(e.clientX, e.currentTarget))}
          onPointerLeave={() => setActive(null)}
        >
          <defs>
            <linearGradient id={gradient} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0" stopColor={color} stopOpacity="0.14" />
              <stop offset="1" stopColor={color} stopOpacity="0.02" />
            </linearGradient>
          </defs>
          {ticks.map((t) => (
            <g key={t} aria-hidden="true">
              <line x1={PAD.left} x2={width - PAD.right} y1={y(t)} y2={y(t)} className={t === 0 ? 'chart-base' : 'chart-grid'} />
              <text x={PAD.left - 8} y={y(t)} className="chart-tick" textAnchor="end" dominantBaseline="middle">
                {format(t)}
              </text>
            </g>
          ))}
          {points.map((p, i) =>
            (i % every === 0 || i === points.length - 1) && (i === points.length - 1 || points.length - 1 - i >= every) ? (
              <text key={p.key} x={x(i)} y={height - 8} className="chart-tick" textAnchor="middle" aria-hidden="true">
                {p.label}
              </text>
            ) : null
          )}
          {lines.map((r, k) => {
            const d = r.map((pt, j) => `${j ? 'L' : 'M'}${x(pt.i)},${y(pt.v)}`).join('');
            const area = `${d}L${x(r[r.length - 1]!.i)},${y(0)}L${x(r[0]!.i)},${y(0)}Z`;
            return (
              <g key={k} aria-hidden="true">
                {/* A wash under one unbroken line; with gaps (no calls) it would read as walls. */}
                {lines.length === 1 && <path d={area} fill={`url(#${gradient})`} />}
                <path d={d} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
                {r.length === 1 && <circle cx={x(r[0]!.i)} cy={y(r[0]!.v)} r={4} fill={color} className="chart-dot" />}
              </g>
            );
          })}
          {lastIdx !== undefined && active === null && (
            <circle cx={x(lastIdx)} cy={y(points[lastIdx]!.value!)} r={4} fill={color} className="chart-dot" aria-hidden="true" />
          )}
          {a && (
            <g aria-hidden="true">
              <line x1={x(active!)} x2={x(active!)} y1={PAD.top} y2={PAD.top + plotH} className="chart-crosshair" />
              {a.value !== null && <circle cx={x(active!)} cy={y(a.value)} r={4.5} fill={color} className="chart-dot" />}
            </g>
          )}
        </svg>
      )}
      {a && (
        <Readout x={x(active!) - PAD.left} plotWidth={plotW}>
          <span className="chart-readout-title">{a.title}</span>
          <span className="chart-readout-row">
            <span className="chart-key line" style={{ background: color }} />
            <strong>{a.value === null ? empty : format(a.value)}</strong>
          </span>
        </Readout>
      )}
    </div>
  );
}

/** Legend for two or more series: the mark's own shape beside the name. */
export function ChartLegend({ series }: { series: ChartSeries[] }) {
  return (
    <ul className="chart-legend">
      {series.map((s) => (
        <li key={s.name}>
          <span className="chart-swatch" style={{ background: s.color }} />
          {s.name}
        </li>
      ))}
    </ul>
  );
}
